import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, Zap, Loader2, ArrowDownLeft, ArrowUpRight, Filter } from "lucide-react";
import { BottomSheet } from "./BottomSheet";
import { EmbeddedCheckoutInline } from "./EmbeddedCheckoutInline";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Global Buy Credits flow. Opens automatically when any AI call is blocked for
// insufficient credits (via the "fv:need-credits" window event), or on demand.
export function BuyCreditsSheet({ open: openProp, onClose: onCloseProp }) {
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null); // insufficient_credits detail (or null)
  const [packs, setPacks] = useState([]);
  const [selected, setSelected] = useState(null); // {client_secret, session_id, credits, amount}
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("buy"); // buy | usage
  const [txns, setTxns] = useState(null);
  const [txFilter, setTxFilter] = useState(0); // 0 all, 1 inbound, 2 outbound

  const loadTxns = async () => {
    setTxns(null);
    try {
      const { data } = await api.get("/ai/credits/transactions");
      setTxns(data.transactions || []);
    } catch { setTxns([]); }
  };

  const controlled = openProp !== undefined;
  const isOpen = controlled ? openProp : open;

  const close = () => {
    setSelected(null);
    setStatus(null);
    if (controlled) onCloseProp && onCloseProp();
    else setOpen(false);
  };

  const loadPacks = async () => {
    try {
      const { data } = await api.get("/ai/credits");
      setPacks(data.packs || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const onNeed = (e) => {
      setStatus(e.detail || {});
      setSelected(null);
      setTab("buy");
      loadPacks();
      if (!controlled) setOpen(true);
    };
    const onOpen = () => {
      setStatus(null);
      setSelected(null);
      setTab("buy");
      loadPacks();
      if (!controlled) setOpen(true);
    };
    window.addEventListener("fv:need-credits", onNeed);
    window.addEventListener("fv:open-credits", onOpen);
    return () => {
      window.removeEventListener("fv:need-credits", onNeed);
      window.removeEventListener("fv:open-credits", onOpen);
    };
  }, [controlled]);

  useEffect(() => {
    if (isOpen && packs.length === 0) loadPacks();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async (packId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/credits/checkout", { pack: packId });
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onPaid = async () => {
    await refreshUser();
    toast.success(`${selected?.credits?.toLocaleString?.() || ""} credits added — they never expire.`);
    close();
  };

  return (
    <BottomSheet open={!!isOpen} onClose={close} title="AI Credits" height="90dvh" fill={!!selected}>
      {selected ? (
        <div className="flex-1 min-h-0 flex flex-col" data-testid="buy-credits-checkout">
          <p className="text-sm mb-3 text-center" style={{ color: "var(--text-secondary)" }}>
            {selected.credits?.toLocaleString?.()} credits · ${selected.amount}
          </p>
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            <EmbeddedCheckoutInline
              clientSecret={selected.client_secret}
              sessionId={selected.session_id}
              onPaid={onPaid}
            />
          </div>
          <button onClick={() => setSelected(null)} data-testid="buy-credits-back"
            className="mt-3 text-xs" style={{ color: "var(--text-secondary)" }}>
            ← Choose a different pack
          </button>
        </div>
      ) : (
        <div className="pt-1 flex flex-col min-h-0" data-testid="credits-sheet">
          {/* Buy / Usage toggle */}
          <div className="flex p-1 rounded-full mb-4 shrink-0" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
            {[["buy", "Buy Credits"], ["usage", "See Usage"]].map(([k, label]) => (
              <button key={k} data-testid={`credits-tab-${k}`}
                onClick={() => { setTab(k); if (k === "usage") loadTxns(); }}
                className="flex-1 rounded-full py-2 text-sm font-semibold transition-colors"
                style={tab === k ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
                {label}
              </button>
            ))}
          </div>

          {tab === "buy" ? (
          <div data-testid="buy-credits-sheet">
          <div className="flex flex-col items-center text-center mb-5">
            <div className="h-14 w-14 rounded-2xl grid place-items-center mb-3"
              style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
              <Sparkles size={26} className="gold-text" />
            </div>
            {status ? (
              <>
                <div className="font-serif-display text-2xl">You're out of AI credits</div>
                <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
                  {status.message || `You've used your monthly allowance. Top up to keep going.`}
                </p>
              </>
            ) : (
              <>
                <div className="font-serif-display text-2xl">Top up your AI credits</div>
                <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
                  Buy a pack for extra AI use across The Vault. Purchased credits never expire.
                </p>
              </>
            )}
          </div>

          <div className="space-y-3">
            {packs.map((p, i) => (
              <motion.button
                key={p.id}
                whileTap={{ scale: 0.98 }}
                disabled={busy}
                onClick={() => buy(p.id)}
                data-testid={`buy-credits-pack-${p.id}`}
                className="w-full card-surface rounded-2xl p-4 flex items-center justify-between text-left relative overflow-hidden"
                style={i === 2 ? { border: "1px solid var(--accent)" } : {}}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
                    style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
                    <Zap size={20} className="gold-text" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl" style={{ color: "var(--text-primary)" }}>
                      {p.credits.toLocaleString()} credits
                    </div>
                    {i === 2 && <div className="text-xs mt-0.5 gold-text font-semibold">Best value</div>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold text-lg" style={{ color: "var(--text-primary)" }}>${p.price}</div>
                </div>
                {busy && <div className="absolute inset-0 grid place-items-center" style={{ background: "color-mix(in srgb, var(--bg-base) 40%, transparent)" }}><Loader2 size={18} className="animate-spin gold-text" /></div>}
              </motion.button>
            ))}
          </div>

          <p className="text-[11px] text-center mt-5" style={{ color: "var(--text-muted)" }}>
            Credits are shared across all Vault AI tools and never expire.
          </p>
          </div>
          ) : (
          <div className="flex flex-col min-h-0 flex-1" data-testid="usage-view">
            {/* 3-state filter switch */}
            <button onClick={() => setTxFilter((f) => (f + 1) % 3)} data-testid="usage-filter-switch"
              className="self-end flex items-center gap-1.5 rounded-full px-3 py-1.5 mb-3 text-xs font-semibold shrink-0"
              style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              <Filter size={13} className="gold-text" /> {["All transactions", "Inbound only", "Outbound only"][txFilter]}
            </button>
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2">
              {txns === null ? (
                <div className="grid place-items-center py-10"><Loader2 size={22} className="animate-spin gold-text" /></div>
              ) : (() => {
                const filtered = txns.filter((t) => txFilter === 0 || (txFilter === 1 && t.direction === "in") || (txFilter === 2 && t.direction === "out"));
                if (filtered.length === 0) return <p className="text-center text-sm py-10" style={{ color: "var(--text-muted)" }}>No transactions yet.</p>;
                return filtered.map((t) => (
                  <div key={t.id} data-testid="usage-txn" className="flex items-center gap-3 card-surface rounded-xl px-3 py-2.5">
                    <div className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                      style={{ background: t.direction === "in" ? "color-mix(in srgb, #4ade80 16%, transparent)" : "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
                      {t.direction === "in" ? <ArrowDownLeft size={16} color="#4ade80" /> : <ArrowUpRight size={16} className="gold-text" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{t.label}</div>
                      <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{t.detail}{t.at ? ` · ${new Date(t.at).toLocaleDateString()}` : ""}</div>
                    </div>
                    <div className="text-sm font-bold shrink-0" style={{ color: t.direction === "in" ? "#4ade80" : "var(--text-secondary)" }}>
                      {t.direction === "in" ? "+" : "−"}{t.amount}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
          )}
        </div>
      )}
    </BottomSheet>
  );
}
